import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide7cMobile from "./HTLDSlide7cMobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide7cContent, slide7cAccentFrom, useSlide7c, Seg } from "./slide7c.data";

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const accent = "#38b5b5";
const font = FONT_BODY;

const S: Record<string, CSSProperties> = {
  root: {
    background: "#040f18",
    fontFamily: font,
    color: "#d0e8f8",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  subtitleBar: {
    background: "#050f1a",
    borderBottom: "1px solid rgba(56,181,181,0.12)",
    padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "clamp(12px,2vw,28px)",
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: "clamp(16px,2vw,28px)",
    padding:
      "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)",
    alignItems: "stretch",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    minWidth: "min(100%, 380px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  panel: {
    width: "clamp(220px,28vw,380px)",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
  },
};

// ─── Limb names (for the mandala phase labels) ─────────────────────────────────
const LIMB_NAMES = [
  "Yama",
  "Niyama",
  "Asana",
  "Pranayama",
  "Pratyahara",
  "Dharana",
  "Dhyana",
  "Samadhi",
];

// One colour per limb: a journey inward — warm outer conduct (gold) through
// the slide's teal to violet, ending in white light at the centre.
const LIMB_RGB: [number, number, number][] = [
  [232, 178, 90],  // Yama        gold
  [228, 146, 108], // Niyama      amber-coral
  [120, 206, 168], // Asana       jade
  [56, 181, 181],  // Pranayama   teal (slide accent)
  [92, 168, 232],  // Pratyahara  sky
  [128, 140, 242], // Dharana     indigo
  [178, 138, 242], // Dhyana      violet
  [244, 244, 255], // Samadhi     white
];
const rgba = (i: number, a: number) =>
  `rgba(${LIMB_RGB[i][0]},${LIMB_RGB[i][1]},${LIMB_RGB[i][2]},${Math.max(0, a).toFixed(3)})`;

// ─── Mandala Canvas ────────────────────────────────────────────────────────────
// Eight layers, one per limb, form from the outside in (Yama → Samadhi), each
// with its own shape. An outer eight-pointed figure has one point per limb
// (in that limb's colour, aimed at its name). Inside: a braided
// ring (Niyama), an eight-pointed star (Asana), a breathing
// wave (Pranayama), inward arrows (Pratyahara), converging points (Dharana),
// the flower of life (Dhyana) and the radiant centre (Samadhi). Motes drift
// inward. Once formed, the limbs are featured one at a time: the layer
// brightens and its name and meaning show below the mandala.
const MandalaCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawFnRef = useRef<null | (() => void)>(null);
  const dtRef     = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    if (!ctx) return;
    const W = 400, H = 560;
    const content = pick(slide7cContent); // slides remount on language change
    const caption = content.canvas.caption;
    const glosses = content.limbs.map(l => l.san);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const cx = 200, cy = 228;
    const MANDALA_SCALE = 0.9; // leaves room between the point tips and the names
    let frame = 0; // 60 units per second

    const PHASE = 70;            // frames per forming layer
    const FORM_END = PHASE * 8;  // all eight formed
    const FEATURE = 240;         // frames each limb is featured afterwards

    const stars = [[26,34,0.9],[362,28,0.8],[40,470,0.7],[336,496,0.8],[372,150,0.6],[28,180,0.6],[200,520,0.7],[90,90,0.5],[318,410,0.5]];

    // Motes drifting inward along a gentle spiral (the turning inward).
    const motes = Array.from({ length: 34 }, (_, k) => ({
      ang: (k / 34) * Math.PI * 2 + Math.random() * 0.4,
      r: 70 + Math.random() * 120,
      speed: 0.12 + Math.random() * 0.12,
      size: 0.7 + Math.random() * 1.1,
    }));

    function layerAmt(i: number): number {
      const t = (frame - i * PHASE) / PHASE;
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    // Which limb is in focus: the one forming, then each in turn.
    function featured(): { i: number; fade: number } {
      if (frame < FORM_END) {
        const i = Math.min(7, Math.floor(frame / PHASE));
        return { i, fade: Math.min(1, (frame - i * PHASE) / 20) };
      }
      const f = frame - FORM_END;
      const i = Math.floor(f / FEATURE) % 8;
      const local = f % FEATURE;
      const fade = Math.min(1, local / 30, (FEATURE - local) / 30);
      return { i, fade };
    }

    const P = (ang: number, r: number): [number, number] => [cx + Math.cos(ang) * r, cy + Math.sin(ang) * r];

    function rotated(rot: number, fn: () => void) {
      ctx.save();
      ctx.translate(cx, cy); ctx.rotate(rot); ctx.translate(-cx, -cy);
      fn();
      ctx.restore();
    }

    function ring(r: number, col: string, w: number) {
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = col; ctx.lineWidth = w; ctx.stroke();
    }

    function glowDot(x: number, y: number, r: number, i: number, a: number) {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r * 3.2);
      g.addColorStop(0, `rgba(255,255,255,${a.toFixed(3)})`);
      g.addColorStop(0.35, rgba(i, a * 0.75));
      g.addColorStop(1, rgba(i, 0));
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(x, y, r * 3.2, 0, Math.PI * 2); ctx.fill();
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#040f18";
      ctx.fillRect(0, 0, W, H);

      stars.forEach(([x, y, r]) => {
        const tw = 0.4 + 0.6 * Math.abs(Math.sin(frame * 0.02 + x));
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(0.4 * tw).toFixed(3)})`; ctx.fill();
      });

      const breath = 0.5 + 0.5 * Math.sin(frame * 0.018); // ~5.8 s cycle
      const spin = frame * 0.003;
      const feat = featured();
      // per-layer brightness: formed amount × spotlight on the featured limb
      const lit = (i: number) => layerAmt(i) * (i === feat.i ? 1 + 0.55 * feat.fade : frame < FORM_END ? 1 : 0.78);

      // soft halo tinted by the featured limb
      const halo = ctx.createRadialGradient(cx, cy, 8, cx, cy, 200);
      halo.addColorStop(0, rgba(feat.i, 0.10 + 0.06 * breath));
      halo.addColorStop(0.55, `rgba(56,181,181,${(0.05 + 0.03 * breath).toFixed(3)})`);
      halo.addColorStop(1, "rgba(56,181,181,0)");
      ctx.fillStyle = halo;
      ctx.beginPath(); ctx.arc(cx, cy, 200, 0, Math.PI * 2); ctx.fill();

      // motes spiral inward, tinted by depth (outer gold → inner white)
      const motesOn = Math.min(1, frame / FORM_END);
      motes.forEach(m => {
        m.r -= m.speed * (dtRef.current > 0 ? dtRef.current * 60 : 1);
        m.ang += 0.0025;
        if (m.r < 16) { m.r = 175 + Math.random() * 20; m.ang = Math.random() * Math.PI * 2; }
        const depth = Math.max(0, Math.min(7, Math.round((175 - m.r) / 22)));
        const fadeIn = Math.min(1, (190 - m.r) / 30), fadeOut = Math.min(1, (m.r - 16) / 30);
        const [x, y] = P(m.ang, m.r);
        ctx.beginPath(); ctx.arc(x, y, m.size, 0, Math.PI * 2);
        ctx.fillStyle = rgba(depth, 0.55 * fadeIn * fadeOut * motesOn);
        ctx.fill();
      });

      ctx.save();
      const bs = MANDALA_SCALE * (1 + 0.02 * breath);
      ctx.translate(cx, cy); ctx.scale(bs, bs); ctx.translate(-cx, -cy);
      ctx.lineCap = "round"; ctx.lineJoin = "round";

      // Outer eight-pointed star — ONE continuous outline. As each limb forms,
      // the line travels on from the previous valley up to that limb's tip
      // (aimed at its name, drawn in its colour) and down to the next valley,
      // so the star is traced in a single clockwise stroke. A spark marks the
      // drawing point; the featured limb's point glows.
      const STAR_TIP = 160, STAR_VALLEY = 116;
      const tipAng = (i: number) => -Math.PI / 2 + i * (Math.PI / 4);
      const tipPt = (i: number) => P(tipAng(i), STAR_TIP);
      const valPt = (i: number) => P(tipAng(i) + Math.PI / 8, STAR_VALLEY); // valley after tip i
      const seg = (i: number, from: [number, number], to: [number, number], u: number, alpha: number, w: number) => {
        if (u <= 0) return null;
        const x = from[0] + (to[0] - from[0]) * u, y = from[1] + (to[1] - from[1]) * u;
        ctx.beginPath(); ctx.moveTo(from[0], from[1]); ctx.lineTo(x, y);
        ctx.strokeStyle = rgba(i, alpha); ctx.lineWidth = w; ctx.stroke();
        return [x, y] as [number, number];
      };
      // faint fill of the whole star once it is complete
      const starDone = layerAmt(7);
      if (starDone > 0) {
        const star = new Path2D();
        for (let i = 0; i < 8; i++) {
          const [tx, ty] = tipPt(i), [vx, vy] = valPt(i);
          i ? star.lineTo(tx, ty) : star.moveTo(tx, ty);
          star.lineTo(vx, vy);
        }
        star.closePath();
        const sg = ctx.createRadialGradient(cx, cy, STAR_VALLEY - 10, cx, cy, STAR_TIP);
        sg.addColorStop(0, "rgba(56,181,181,0)");
        sg.addColorStop(1, `rgba(120,206,190,${(0.10 * starDone).toFixed(3)})`);
        ctx.fillStyle = sg; ctx.fill(star);
      }
      for (let i = 0; i < 8; i++) {
        const pa = layerAmt(i);
        if (pa <= 0) continue;
        const on = i === feat.i && frame >= FORM_END ? feat.fade : 0;
        const alpha = 0.7 + 0.3 * on, w = 1.4 + 0.7 * on;
        const up = Math.min(1, pa * 2), down = Math.max(0, pa * 2 - 1);
        const head1 = seg(i, valPt((i + 7) % 8), tipPt(i), up, alpha, w);
        const head2 = seg(i, tipPt(i), valPt(i), down, alpha, w);
        if (up >= 1) { const [tx, ty] = tipPt(i); glowDot(tx, ty, 1.8 + 0.8 * on, i, (0.8 + 0.2 * on)); }
        const head = pa < 1 ? (head2 ?? head1) : null;
        if (head) glowDot(head[0], head[1], 2.2, i, 0.95); // the travelling spark
      }

      // 1 · Yama — the gold ring the eight points rise from
      let a = lit(0);
      if (a > 0) {
        ring(STAR_VALLEY + 2, rgba(0, 0.45 * a), 1.2);
        ring(STAR_VALLEY - 4, rgba(0, 0.22 * a), 0.8);
      }

      // 2 · Niyama — a braided ring: three interwoven strands of steady practice
      a = lit(1);
      if (a > 0) rotated(-spin * 0.6, () => {
        for (let k = 0; k < 3; k++) {
          ctx.beginPath();
          for (let st = 0; st <= 240; st++) {
            const th = (st / 240) * Math.PI * 2;
            const r = 104 + 5 * Math.sin(9 * th + (k * Math.PI * 2) / 3);
            const [x, y] = P(th, r);
            st ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          }
          ctx.closePath();
          ctx.strokeStyle = rgba(1, (0.75 - k * 0.15) * a); ctx.lineWidth = 1.3; ctx.stroke();
        }
        ring(97, rgba(1, 0.22 * a), 0.8);
        ring(111, rgba(1, 0.22 * a), 0.8);
      });

      // 3 · Asana — eight-pointed star (two squares): steadiness
      a = lit(2);
      if (a > 0) rotated(spin * 0.35, () => {
        ring(93, rgba(2, 0.4 * a), 1);
        for (let s = 0; s < 2; s++) {
          ctx.beginPath();
          for (let k = 0; k < 4; k++) {
            const [x, y] = P(s * Math.PI / 4 + k * Math.PI / 2, 91);
            k ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          }
          ctx.closePath();
          ctx.fillStyle = rgba(2, 0.06 * a); ctx.fill();
          ctx.strokeStyle = rgba(2, 0.65 * a); ctx.lineWidth = 1.3; ctx.stroke();
        }
        for (let k = 0; k < 8; k++) { const [x, y] = P(k * Math.PI / 4, 91); glowDot(x, y, 1.6, 2, 0.8 * a); }
      });

      // 4 · Pranayama — two wave rings that swell with the breath
      a = lit(3);
      if (a > 0) {
        for (let w = 0; w < 2; w++) {
          ctx.beginPath();
          for (let s = 0; s <= 180; s++) {
            const th = (s / 180) * Math.PI * 2;
            const r = 78 + (3 + 4 * breath) * Math.sin(12 * th + frame * 0.03 * (w ? -1 : 1) + w * Math.PI);
            const [x, y] = P(th, r);
            s ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          }
          ctx.closePath();
          ctx.strokeStyle = rgba(3, (w ? 0.35 : 0.7) * a); ctx.lineWidth = w ? 1 : 1.5; ctx.stroke();
        }
      }

      // 5 · Pratyahara — eight arrows pointing inward: senses turn within
      a = lit(4);
      if (a > 0) rotated(-spin * 0.9, () => {
        for (let k = 0; k < 8; k++) {
          const an = (k + 0.5) * Math.PI / 4;
          const [tx, ty] = P(an, 54), [lx, ly] = P(an - 0.18, 69), [rx, ry] = P(an + 0.18, 69);
          ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(tx, ty); ctx.lineTo(rx, ry); ctx.closePath();
          ctx.fillStyle = rgba(4, 0.2 * a); ctx.fill();
          ctx.strokeStyle = rgba(4, 0.7 * a); ctx.lineWidth = 1.2; ctx.stroke();
        }
      });

      // 6 · Dharana — points converging on the centre: one-pointed focus
      a = lit(5);
      if (a > 0) {
        ring(55, rgba(5, 0.3 * a), 0.8);
        for (let k = 0; k < 16; k++) {
          const an = (k * Math.PI * 2) / 16 + spin * 0.6;
          for (let d = 0; d < 3; d++) {
            const u = ((frame * 0.006 + d / 3 + (k % 2) * 0.16) % 1); // 0 → 1 inward
            const [x, y] = P(an, 54 - u * 16);
            ctx.beginPath(); ctx.arc(x, y, 1.4 - u * 0.6, 0, Math.PI * 2);
            ctx.fillStyle = rgba(5, (0.85 - 0.6 * u) * a); ctx.fill();
          }
        }
      }

      // 7 · Dhyana — flower of life: six circles, one unbroken flow
      a = lit(6);
      if (a > 0) rotated(spin * 1.2, () => {
        ring(34, rgba(6, 0.5 * a), 1);
        for (let k = 0; k < 6; k++) {
          const [x, y] = P(k * Math.PI / 3, 17);
          ctx.beginPath(); ctx.arc(x, y, 17, 0, Math.PI * 2);
          ctx.fillStyle = rgba(6, 0.05 * a); ctx.fill();
          ctx.strokeStyle = rgba(6, 0.6 * a); ctx.lineWidth = 1; ctx.stroke();
        }
      });

      // 8 · Samadhi — the radiant bindu
      a = layerAmt(7);
      if (a > 0) {
        const glowBoost = feat.i === 7 ? 1 + 0.4 * feat.fade : 1;
        const R8 = (20 + 6 * breath) * glowBoost;
        const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, R8);
        core.addColorStop(0, `rgba(255,255,255,${(0.98 * a).toFixed(3)})`);
        core.addColorStop(0.4, `rgba(236,232,255,${(0.7 * a).toFixed(3)})`);
        core.addColorStop(1, "rgba(200,190,255,0)");
        ctx.fillStyle = core; ctx.beginPath(); ctx.arc(cx, cy, R8, 0, Math.PI * 2); ctx.fill();
        rotated(spin * 0.5, () => {
          for (let k = 0; k < 16; k++) {
            const an = (k * Math.PI * 2) / 16, len = (k % 2 ? 10 : 16) + 5 * breath;
            ctx.beginPath(); ctx.moveTo(cx, cy);
            const [x, y] = P(an, len);
            ctx.lineTo(x, y);
            ctx.strokeStyle = `rgba(255,255,255,${(0.35 * a).toFixed(3)})`; ctx.lineWidth = 0.8; ctx.stroke();
          }
        });
        ctx.beginPath(); ctx.arc(cx, cy, 3.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`; ctx.fill();
      }

      ctx.restore();

      // ── Limb names around the mandala, each in its own colour ──
      const LABEL_R = 172;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      for (let i = 0; i < 8; i++) {
        const wa = layerAmt(i);
        if (wa <= 0) continue;
        const ang = -Math.PI / 2 + i * (Math.PI / 4);
        const [lx, ly] = P(ang, LABEL_R);
        const on = i === feat.i;
        ctx.font = `${on ? 700 : 500} ${on ? 11.5 : 10}px 'JetBrains Mono','Courier New',monospace`;
        ctx.fillStyle = rgba(i, (on ? 0.6 + 0.4 * feat.fade : 0.55) * wa);
        ctx.fillText(LIMB_NAMES[i].toUpperCase(), lx, ly);
      }

      // ── Featured limb: name + meaning under the mandala ──
      const fa = feat.fade * layerAmt(feat.i);
      if (fa > 0) {
        ctx.textBaseline = "alphabetic";
        ctx.font = "11px 'JetBrains Mono','Courier New',monospace";
        ctx.fillStyle = `rgba(160,200,210,${(0.55 * fa).toFixed(3)})`;
        ctx.fillText(`${feat.i + 1} / 8`, cx, 434);
        ctx.font = "600 24px 'Space Grotesk',Arial,sans-serif";
        ctx.fillStyle = rgba(feat.i, 0.95 * fa);
        ctx.fillText(LIMB_NAMES[feat.i], cx, 462);
        ctx.font = "14px 'Space Grotesk',Arial,sans-serif";
        ctx.fillStyle = `rgba(205,224,242,${(0.8 * fa).toFixed(3)})`;
        ctx.fillText(glosses[feat.i] ?? "", cx, 484);
      }

      ctx.textBaseline = "alphabetic";
      ctx.font = "13px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = "rgba(56,181,181,0.42)";
      ctx.fillText(caption, cx, H - 22);

      frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }
    drawFnRef.current = draw;
    return () => { drawFnRef.current = null; };
  }, []);

useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          borderRadius: 8,
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    </div>
  );
};

// ─── Slide content ─────────────────────────────────────────────────────────────
const EightLimbsInner: React.FC = () => {
  const fontLinkRef = useRef<HTMLLinkElement | null>(null);

  useEffect(() => {
    const existing = document.querySelector(
      'link[href*="Space+Grotesk"]'
    ) as HTMLLinkElement | null;
    if (!existing) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(link);
      fontLinkRef.current = link;
    }
    return () => {
      if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
        document.head.removeChild(fontLinkRef.current);
        fontLinkRef.current = null;
      }
    };
  }, []);

  const lbl: CSSProperties = {
    fontSize: "clamp(8px,0.7vw,11px)",
    fontFamily: FONT_MONO,
    color: accent,
    letterSpacing: 3,
    textTransform: "uppercase",
    marginBottom: "clamp(4px,0.55vw,8px)",
    fontStyle: "normal",
  };
  const txt: CSSProperties = {
    fontSize: "clamp(11px,0.95vw,14px)",
    fontStyle: "normal",
    fontFamily: FONT_BODY,
    lineHeight: 1.6,
    color: "#cde0f2",
    margin: 0,
  };

  // limb name + the traits / essence, woven with food · thought · rest
  const { meta, limbs: limbRows, intro, callout } = useSlide7c();

  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <div style={S.root} ref={rootRef}>
      <SideStarField rootRef={rootRef} />

      {/* Header row */}
      <div style={S.subtitleBar}>
        <div
          style={{
            fontSize: "clamp(11px,1.0vw,14px)",
            fontStyle: "normal",
            color: "#7aa8cc",
            fontFamily: FONT_BODY,
          }}
        >
          {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
          <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
        </div>
        <div
          style={{
            fontSize: "clamp(16px,1.9vw,28px)",
            fontStyle: "normal",
            fontWeight: 600,
            color: "#e8f4ff",
            fontFamily: FONT_DISPLAY,
            letterSpacing: 0.5,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {meta.headerTitlePlain}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
        </div>
      </div>

      <div style={S.body}>
        <div style={S.content}>
          <div>
            <div
              style={{
                fontSize: "clamp(8px,0.68vw,11px)",
                fontFamily: FONT_MONO,
                color: "rgba(56,181,181,0.55)",
                letterSpacing: 3,
                marginBottom: "clamp(4px,0.5vw,7px)",
              }}
            >
              {meta.sanskritOverline}
            </div>
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: "clamp(18px,2.2vw,28px)",
                fontStyle: "normal",
                fontWeight: 600,
                color: "#e8f4ff",
                margin: "0 0 clamp(2px,0.35vw,6px)",
                lineHeight: 1.2,
              }}
            >
              {meta.titlePre}
              <span style={{ color: accent }}>{meta.titleAccentWord}</span>
              {meta.titlePost}
            </h1>
            <p
              style={{
                fontFamily: FONT_BODY,
                fontSize: "clamp(11px,1.0vw,14px)",
                fontStyle: "normal",
                fontWeight: 300,
                color: "#9bbdd6",
                margin: 0,
                letterSpacing: 0.3,
              }}
            >
              {meta.subtitle}
            </p>
            <p
              style={{
                ...txt,
                marginTop: "clamp(6px,0.8vw,10px)",
                color: "#aacdd8",
              }}
            >
              {renderSegs(intro, "#bfeaea")}
            </p>
          </div>

          {/* The eight limbs as a compact narrative list */}
          <div
            style={{
              borderTop: "1px solid rgba(56,181,181,0.10)",
              borderBottom: "1px solid rgba(56,181,181,0.10)",
              paddingTop: "clamp(4px,0.5vw,7px)",
              paddingBottom: "clamp(4px,0.5vw,7px)",
              display: "flex",
              flexDirection: "column",
              gap: "clamp(2px,0.3vw,4px)",
            }}
          >
            {limbRows.map((l, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "clamp(7px,0.9vw,12px)",
                  alignItems: "baseline",
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: "clamp(78px,9vw,120px)",
                    fontFamily: FONT_BODY,
                    fontStyle: "normal",
                    fontWeight: 700,
                    fontSize: "clamp(11px,0.92vw,14px)",
                    color: i >= slide7cAccentFrom ? accent : "#a0dcdc",
                  }}
                >
                  {l.n}
                </span>
                <span style={{ ...txt, lineHeight: 1.4 }}>
                  <span style={{ color: "#bfeaea", fontWeight: 700 }}>
                    {l.san}.
                  </span>{" "}
                  <span style={{ fontWeight: 300, color: "#9bbdd6" }}>
                    {l.desc}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              background: "rgba(56,181,181,0.06)",
              border: "1px solid rgba(56,181,181,0.22)",
              borderRadius: 8,
              padding: "clamp(7px,0.85vw,12px) clamp(13px,1.5vw,20px)",
            }}
          >
            {/* Label left, source right — one line, so the slide fits */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
              <div style={lbl}>{meta.calloutLabel}</div>
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: "clamp(8px,0.68vw,10px)",
                  color: "rgba(120,200,200,0.5)",
                  letterSpacing: 1.5,
                  textAlign: "right",
                }}
              >
                {meta.attribution}
              </div>
            </div>
            <p style={{ ...txt, color: "#9adcdc" }}>
              {renderSegs(callout, "#c8f0f0")}
            </p>
          </div>
        </div>

        {/* Panel — forming mandala fills the full panel height */}
        <div style={S.panel}>
          <MandalaCanvas />
        </div>
      </div>
    </div>
  );
};

const HTLDSlide7c: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide7cMobile />
    : <ScaleToFit><EightLimbsInner /></ScaleToFit>;
};

export default HTLDSlide7c;
